import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchHostingWebhookDeliveries, retryHostingWebhookDelivery
} from "src/actions/mine_craft/hostingActions";
import { useAppDispatch } from "src/store/hooks";
import usePermissions from "src/hooks/usePermissions";
import { formattedDate } from "src/helpers/DateHelpers";

import { Button, Segmented, Select, Table, Tag, message } from 'antd';
import { RedoOutlined, SyncOutlined } from '@ant-design/icons';
import Card from "src/componets/Card";
import { shortFromNow } from "../../Servers/_partials/_serverHelpers";

interface WebhookDelivery {
  id: string;
  event: string;
  url: string;
  success: boolean;
  attempts: number;
  lastStatus: number | null;
  lastError: string | null;
  createdAt: string;
  payload: any;
}

const PAGE_SIZE = 25;

// Every webhook event crafty knows how to send us.
const EVENTS = [
  'server.ready', 'server.failed', 'server.crashed', 'server.deleted',
  'content.installed', 'content.failed', 'webhook.test'
];

type SuccessFilter = 'all' | 'failed' | 'delivered';

// crafty-servers' delivery journal for our webhook endpoint — the place
// to look when a server hangs in `installing` (30-day retention). Failed
// deliveries can be re-sent with the identical, normally signed payload.
const _WebhookDeliveries = () => {
  const dispatch = useAppDispatch();
  const { write_minecraft } = usePermissions();

  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [event, setEvent] = useState<string | undefined>(undefined);
  const [outcome, setOutcome] = useState<SuccessFilter>('all');

  const load = useCallback(async (targetPage: number, eventFilter?: string, outcomeFilter?: SuccessFilter) => {
    setLoading(true);
    const resultAction = await dispatch(fetchHostingWebhookDeliveries({
      limit:   PAGE_SIZE,
      offset:  (targetPage - 1) * PAGE_SIZE,
      event:   eventFilter,
      success: outcomeFilter === 'all' || outcomeFilter === undefined
        ? undefined
        : outcomeFilter === 'delivered'
    }));
    setLoading(false);

    if (fetchHostingWebhookDeliveries.fulfilled.match(resultAction)) {
      setDeliveries(resultAction.payload?.deliveries || []);
      setTotal(resultAction.payload?.total || 0);
    }
  }, [dispatch]);

  useEffect(() => {
    load(1, undefined, 'all').catch(console.error);
  }, [load]);

  function refetch(nextPage: number, nextEvent?: string, nextOutcome?: SuccessFilter) {
    setPage(nextPage);
    load(nextPage, nextEvent, nextOutcome).catch(console.error);
  }

  async function retry(delivery: WebhookDelivery) {
    setRetrying(delivery.id);
    const resultAction = await dispatch(retryHostingWebhookDelivery({ deliveryId: delivery.id }));
    setRetrying(null);

    if (retryHostingWebhookDelivery.fulfilled.match(resultAction)) {
      const delivered = resultAction.payload?.delivered;
      delivered
        ? message.success('Delivered')
        : message.warning('Re-sent, but the endpoint still refused it');
      load(page, event, outcome).catch(console.error);
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Retry failed');
    }
  }

  const columns = [
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      render: (name: string) => <Tag>{name}</Tag>
    },
    {
      title: 'Outcome',
      key: 'outcome',
      render: (_: unknown, row: WebhookDelivery) => (
        <span className='o-outcome'>
          {row.success ? <Tag color='green'>delivered</Tag> : <Tag color='red'>failed</Tag>}
          {row.lastStatus !== null && <span className='o-status'>HTTP {row.lastStatus}</span>}
        </span>
      )
    },
    {
      title: 'Attempts',
      dataIndex: 'attempts',
      key: 'attempts',
      width: 90
    },
    {
      title: 'Error',
      dataIndex: 'lastError',
      key: 'lastError',
      ellipsis: true,
      render: (error: string | null) => error || '-'
    },
    {
      title: 'When',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (iso: string) =>
        iso ? <span title={formattedDate(iso).dateTime}>{shortFromNow(iso)}</span> : '-'
    },
    ...(write_minecraft ? [ {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: unknown, row: WebhookDelivery) => (
        <Button
          size='small'
          icon={<RedoOutlined />}
          loading={retrying === row.id}
          onClick={() => retry(row)}
        >
          Retry
        </Button>
      )
    } ] : [])
  ];

  return (
    <Card className='v-hosting-deliveries'>
      <div className='o-head'>
        <h4 className='v-content-title'>Webhook deliveries</h4>
        <span className='o-count'>{total}</span>

        <Select
          className='o-event-filter'
          allowClear
          placeholder='All events'
          value={event}
          options={EVENTS.map(name => ({ value: name, label: name }))}
          onChange={value => { setEvent(value); refetch(1, value, outcome); }}
        />

        <Segmented
          size='small'
          value={outcome}
          options={[
            { value: 'all', label: 'All' },
            { value: 'failed', label: 'Failed' },
            { value: 'delivered', label: 'Delivered' }
          ]}
          onChange={value => { setOutcome(value as SuccessFilter); refetch(1, event, value as SuccessFilter); }}
        />

        <Button
          className='o-icon-btn'
          icon={<SyncOutlined />}
          title='Refresh'
          aria-label='Refresh deliveries'
          loading={loading}
          onClick={() => load(page, event, outcome)}
        />
      </div>

      <Table
        size='small'
        rowKey='id'
        loading={loading && !deliveries.length}
        columns={columns}
        dataSource={deliveries}
        expandable={{
          expandedRowRender: row => (
            <pre className='o-payload'>{JSON.stringify(row.payload, null, 2)}</pre>
          ),
          rowExpandable: row => !!row.payload
        }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          hideOnSinglePage: true,
          showSizeChanger: false,
          onChange: nextPage => refetch(nextPage, event, outcome)
        }}
      />

      <div className='o-hint'>
        30-day retention on crafty-servers' side. A retry re-sends the identical
        payload, normally signed, with an <code>X-Crafty-Redelivery</code> header.
      </div>
    </Card>
  );
}

export default _WebhookDeliveries;
